import { showNotification } from '@mantine/notifications';
import { RemoteSourceGetResponse } from 'api/actions/remote-source-get/remote-source-get-response';
import { useApi } from 'api/api-context';
import { isAxiosError } from 'axios';
import ChastiaConfigForm from 'components/forms/remote-source/config/chastia-config-form/ChastiaConfigForm';
import { RemoteSourceConfig } from 'components/forms/remote-source/config/types';
import XlsxConfigForm from 'components/forms/remote-source/config/xlsx-config-form/XlsxConfigForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { REMOTE_SOURCES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Configuration page for syncing with Chastia.
 */
export default function ConfigRemoteSourcePage() {
  const { getAction } = useApi();
  const [remoteSource, setRemoteSource] = useState<RemoteSourceGetResponse | null>(null);
  const { remoteSourceId = '' } = useParams();
  const initialValues = useMemo(() => JSON.parse(remoteSource?.config ?? 'null') || undefined, [remoteSource]);
  const isXlsx = remoteSource?.type === 'xlsx';

  const handleSubmit = useCallback(
    (config: RemoteSourceConfig) => {
      const updateConfig = getAction('RemoteSourceUpdateConfig');

      updateConfig({ parameters: { remoteSourceId }, payload: { config: JSON.stringify(config) } })
        .then(() =>
          showNotification({
            title: 'Konfigurácia externého zdroja bola úspešne uložená',
            message: '',
            color: SUCCESS_NOTIFICATION_COLOR,
          })
        )
        .catch((error: Error) => {
          if (isAxiosError(error) && error.response?.data?.error?.code === 4000003) {
            showNotification({
              title: 'Konfigurácia sa neuložila',
              message: error.response.data.error.message,
              color: 'red',
            });
          } else {
            panic(error);
          }
        });
    },
    [getAction, remoteSourceId]
  );

  useEffect(() => {
    const getRemoteSource = getAction('RemoteSourceGet');
    getRemoteSource({ parameters: { remoteSourceId } }).then(setRemoteSource).catch(panic);
  }, [remoteSourceId, getAction]);

  return (
    <DashboardLayout
      title="Konfigurácia externého zdroja"
      breadcrumbs={[
        { title: 'Externé zdroje', link: REMOTE_SOURCES_PAGE_PATH.original },
        { title: 'Konfigurácia externého zdroja' },
      ]}
    >
      {remoteSource &&
        (isXlsx ? (
          <XlsxConfigForm initialValues={initialValues} onSubmit={handleSubmit} />
        ) : (
          <ChastiaConfigForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            remoteSourceId={remoteSource.remoteSourceId}
          />
        ))}
    </DashboardLayout>
  );
}
